//TODO : Refactor after ai
/**
 * Daten-Bereich: alle 31 Objekte als Liste und Detail im neuen Design – Vollständigkeit und Stammdaten.
 * Die Module (Cockpit, Kunde, Aufnahme …) sind die eigentliche Arbeitsoberfläche.
 */
import { useNavigate, useParams } from "react-router-dom";
import { useDatenStore } from "@app/store";
import {
  alleKlassen,
  datensatz,
  datensaetze,
  formatWert,
  objektKlasse,
  relation as relationVon,
  NAVIGATIONSBEREICHE,
  type Datensatz,
} from "@shared/demo-data/viacid";
import { ch, klassenName, viele } from "./lib";
import { Hinweis, Karte, Pille, Seite, Zeile } from "./ui";
import { ActionLeiste, AnlegenKnopf, Statusleiste } from "./ActionLeiste";

const MODUL_LINK: Record<string, (d: Datensatz) => string> = {
  kunde: (d) => `/app/kunden/${d.id}`,
  verkaufschance: (d) => `/app/chancen/${d.id}`,
  angebot: (d) => `/app/angebote/${d.id}`,
  baustellenaufnahme: (d) => `/app/aufnahme/${d.id}`,
  einsatz: (d) => `/app/baustelle/${d.id}`,
  auftrag: (d) => `/app/auftraege/${d.id}`,
};

export function DatenUebersichtPage() {
  const b = useDatenStore((s) => s.bestand);
  const nav = useNavigate();
  return (
    <Seite
      titel="Daten"
      untertitel="Alle Objekte des Datenmodells (31) · Stammdaten pflegen, Vorgänge nachschlagen"
    >
      <div className="vc-grid c3">
        {NAVIGATIONSBEREICHE.map((nb) => (
          <Karte key={nb.key} titel={nb.label}>
            <div className="vc-list">
              {alleKlassen()
                .filter((k) => k.navigation === nb.key)
                .sort((x, y) => x.prioritaet - y.prioritaet)
                .map((k) => (
                  <Zeile
                    key={k.slug}
                    titel={ch(k.plural)}
                    meta={k.beschreibung}
                    rechts={<Pille>{datensaetze(b, k.slug).length}</Pille>}
                    onClick={() => nav(`/app/daten/${k.slug}`)}
                  />
                ))}
            </div>
          </Karte>
        ))}
      </div>
    </Seite>
  );
}

export function DatenListePage() {
  const { slug = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const nav = useNavigate();
  const k = objektKlasse(slug);
  if (!k) return <Hinweis ton="red">Unbekanntes Objekt.</Hinweis>;
  const liste = datensaetze(b, slug);
  const spalten = k.felder.slice(0, 5);
  return (
    <Seite
      titel={ch(k.plural)}
      untertitel={k.beschreibung}
      aktionen={k ? <AnlegenKnopf klasse={k} /> : undefined}
    >
      <Karte>
        <table className="vc-table">
          <thead>
            <tr>
              <th>Titel</th>
              {spalten.map((f) => (
                <th key={f.key}>{ch(f.label)}</th>
              ))}
              {k.zustaende.length > 0 && <th>Status</th>}
            </tr>
          </thead>
          <tbody>
            {liste.map((d) => (
              <tr
                key={d.id}
                className="link"
                onClick={() =>
                  nav(MODUL_LINK[slug]?.(d) ?? `/app/daten/${slug}/${d.id}`)
                }
              >
                <td>
                  <strong>{d.titel}</strong>
                  <div className="sub">{d.untertitel}</div>
                </td>
                {spalten.map((f) => (
                  <td key={f.key}>{formatWert(f, d.werte[f.key])}</td>
                ))}
                {k.zustaende.length > 0 && (
                  <td>
                    <Pille status={d.status} />
                  </td>
                )}
              </tr>
            ))}
            {liste.length === 0 && (
              <tr>
                <td colSpan={7} className="sub">
                  Keine Datensätze.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Karte>
    </Seite>
  );
}

export function DatenDetailPage() {
  const { slug = "", id = "" } = useParams();
  const b = useDatenStore((s) => s.bestand);
  const nav = useNavigate();
  const k = objektKlasse(slug);
  const d = k ? datensatz(b, slug, id) : undefined;
  if (!k || !d) return <Hinweis ton="red">Datensatz nicht gefunden.</Hinweis>;
  const ausgehend = Object.entries(d.bezuege).map(([rid, ids]) => ({
    rel: relationVon(rid),
    rid,
    ids: viele(ids),
  }));
  const eingehend = alleKlassen().flatMap((kl) =>
    datensaetze(b, kl.slug).flatMap((x) =>
      Object.entries(x.bezuege)
        .filter(([, ids]) => viele(ids).includes(d.id))
        .map(([rid]) => ({ satz: x, slug: kl.slug, rid }))
    )
  );
  return (
    <Seite
      titel={d.titel}
      untertitel={
        <>
          {klassenName(slug)} · {d.untertitel}{" "}
          {d.status && <Pille status={d.status} />}
        </>
      }
      aktionen={<ActionLeiste klasse={k} satz={d} />}
    >
      <Statusleiste klasse={k} satz={d} />
      <div className="vc-grid main-side">
        <Karte titel="Angaben">
          <table className="vc-table">
            <tbody>
              {k.felder.map((f) => (
                <tr key={f.key}>
                  <th style={{ width: "40%" }}>{ch(f.label)}</th>
                  <td>{formatWert(f, d.werte[f.key])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Karte>
        <div className="vc-grid">
          <Karte titel="Verweise">
            <div className="vc-list">
              {ausgehend.map(({ rel, rid, ids }) =>
                ids.map((zid) => {
                  const zs = rel ? datensatz(b, rel.zu, zid) : undefined;
                  return (
                    <Zeile
                      key={rid + zid}
                      titel={zs?.titel ?? zid}
                      meta={ch(rel?.label ?? rid)}
                      onClick={
                        zs && rel
                          ? () =>
                              nav(
                                MODUL_LINK[rel.zu]?.(zs) ??
                                  `/app/daten/${rel.zu}/${zs.id}`
                              )
                          : undefined
                      }
                    />
                  );
                })
              )}
              {ausgehend.length === 0 && (
                <div className="sub">Keine Verweise.</div>
              )}
            </div>
          </Karte>
          <Karte titel="Verwendet von">
            <div className="vc-list">
              {eingehend.map(({ satz, slug: s, rid }) => (
                <Zeile
                  key={rid + satz.id}
                  titel={satz.titel}
                  meta={`${klassenName(s)} · ${ch(relationVon(rid)?.name ?? rid)}`}
                  rechts={
                    satz.status ? <Pille status={satz.status} /> : undefined
                  }
                  onClick={() =>
                    nav(MODUL_LINK[s]?.(satz) ?? `/app/daten/${s}/${satz.id}`)
                  }
                />
              ))}
              {eingehend.length === 0 && <div className="sub">—</div>}
            </div>
          </Karte>
        </div>
      </div>
    </Seite>
  );
}
